"use client";

import { EllipsisVertical } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUrlParam from "~/hooks/useUrlParam";
import { cn } from "~/lib/utils";
import {
  VIDEO_ALL_SORT_COLUMNS,
  VIDEO_SORT_COLUMNS,
} from "~/server/db/videos/constants";
import { type OrderDirEnum } from "~/types/db";
import { type VideoSortColumn } from "~/types/videos";

const ENGLISH_SORT_COLUMNS = {
  PUBLISHED_ON: "Published",
  ADDED_ON: "Added By You",
  LIKES: "Likes",
  VIEWS: "Views",
} as const;

const MyVideoSortColumns = () =>
  Object.entries(VIDEO_SORT_COLUMNS).map(([key, value]) => (
    <DropdownMenuRadioItem disabled key={value} value={value}>
      {ENGLISH_SORT_COLUMNS[key as keyof typeof ENGLISH_SORT_COLUMNS]}
    </DropdownMenuRadioItem>
  ));

const AllVideoSortColumns = () =>
  Object.entries(VIDEO_ALL_SORT_COLUMNS).map(([key, value]) => (
    <DropdownMenuRadioItem key={value} value={value}>
      {ENGLISH_SORT_COLUMNS[key as keyof typeof ENGLISH_SORT_COLUMNS]}
    </DropdownMenuRadioItem>
  ));

const SortOrder = ({ isChronological }: { isChronological: boolean }) => (
  <>
    <DropdownMenuRadioItem value="desc">
      {!isChronological ? "Most" : "Newest"} first
    </DropdownMenuRadioItem>
    <DropdownMenuRadioItem value="asc">
      {!isChronological ? "Least" : "Oldest"} first
    </DropdownMenuRadioItem>
  </>
);

export default function VideoListControls({
  className,
  discover,
  totalCount,
  orderBy: initialOrderBy,
  orderDir: initialOrderDir,
}: {
  className?: string;
  discover?: boolean;
  totalCount?: number;
  orderBy: VideoSortColumn;
  orderDir: OrderDirEnum;
}) {
  const [orderBy, setOrderBy] = useUrlParam<string>("orderBy", initialOrderBy);
  const [orderDirection, setOrderDirection] = useUrlParam<string>(
    "orderDir",
    initialOrderDir,
  );

  const isMyVideos = !discover;

  return (
    <div
      className={cn("mb-2 flex items-center justify-between px-4", className)}
    >
      <div className="text-sm">{totalCount} videos</div>
      <div className="flex">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-sm">
              <EllipsisVertical className="h-3.5 w-3.5" />
              <span className="sr-only">Filter</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Order</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup value={orderBy} onValueChange={setOrderBy}>
              {isMyVideos ? <MyVideoSortColumns /> : <AllVideoSortColumns />}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={orderDirection}
              onValueChange={setOrderDirection}
            >
              <SortOrder
                isChronological={
                  orderBy === VIDEO_ALL_SORT_COLUMNS.PUBLISHED_ON ||
                  orderBy === VIDEO_SORT_COLUMNS.ADDED_ON
                }
              />
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
